import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Button, DateRangePicker, PageHeader, Pill, Table } from '@demandlocal/components'
import LineChart from '../../../../../components/Charts/LineChart'
import { ReactComponent as DownloadIcon } from '../../../../../images/icons/action_download.svg'
import { ReactComponent as VehicleIcon } from '../../../../../images/icons/vehicle_single.svg'
import { useParams } from 'react-router'
import { formatCurrency, formatNumber } from '../../../../../lib/utils/formatNumber'
import { createColumnHelper } from '@tanstack/react-table'
import { Vehicle } from '../../../../../lib/types/Vehicle'
import { Link, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useGetDynamicDisplayCampaignVehicleMetrics } from '../../../../../api/dealership/campaigns/reports/useGetDynamicDisplayCampaignVehicleMetrics'
import { useDownloadPDFReport } from '../../../../../api/dealership/reports/useDownloadPDFReport'
import toast from 'react-hot-toast'
import { DateRange } from '../../../../../lib/types/DateRange'
import { useGetDisplayCampaignById } from '../../../../../api/dealership/campaigns/useGetDisplayCampaignById'
import { useGetDynamicDisplayTopVehicles } from '../../../../../api/dealership/campaigns/reports/useGetDynamicDisplayTopVehicles'
import { DisplayCreative } from '../../../../../lib/types/DisplayCreativeMetrics'
import { useGetDisplayCreativeMetrics } from '../../../../../api/dealership/campaigns/reports/useGetDisplayCreativeMetrics'
import { take, uniqBy } from 'lodash'
import ConversionAttributionMetrics from '../../../../../components/Campaigns/Reports/ConversionAttributionMetrics'
import { useGetPerformanceStats } from '../../../../../api/dealership/useGetPerformanceStats'
import { GA4 } from '../../../../../lib/utils/ga4'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import mtm from '../../../../../lib/utils/mtm'
import { useDateRange } from '../../../../../store/zustand/dateRangeStore'
import downloadCSV from '../../../../../api/downloadCSV'
import { useGetDYDReport } from '../../../../../api/dealership/campaigns/reports/useGetDYDReport'
import { useGetCampaignBudget } from '../../../../../api/dealership/campaigns/useGetCampaignBudget'
import { useGetAgencyByAgencyId } from '../../../../../api/agencies/useGetAgencyByAgencyId'
import dayjs from 'dayjs'
import { useGetCampaignCallRecordings } from '../../../../../api/dealership/campaigns/reports/useGetCampaignCallRecordings'
import SiteListTable from '../../../../../components/Campaigns/Reports/SiteListWithAdSizeBreakout'

const columnHelper = createColumnHelper<Vehicle>()
const callsColumnHelper = createColumnHelper<{
  phone_number: string
  recording: string
  datetime: string
  duration: string
  phone_call_vendor_id: string
}>()

const DynamicDisplayCampaignTemplate = ({
  children,
  filters,
  setFilters,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
}>): JSX.Element => {
  const { campaign_id } = useParams()
  const { setDateRange } = useDateRange()
  const dealership = useDealership()
  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()

  const { data: campaign } = useGetDisplayCampaignById({
    campaign_id: campaign_id,
  })
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const { data: campaignBudget } = useGetCampaignBudget({
    campaign_id,
    filters: {
      start_date: filters?.dateRange.startDate,
      end_date: filters?.dateRange.endDate,
    },
  })
  const {
    data: {
      overallMetrics = {
        impressions: 0,
        clicks: 0,
        conversions: 0,
        sales: 0,
        booked_impressions: 0,
      },
    } = {},
  } = useGetDYDReport({
    campaign_id: campaign_id,
    filters: {
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  const { data: phoneCalls } = useGetCampaignCallRecordings({
    filters: {
      campaign_id: campaign_id,
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  return (
    <div className={'flex flex-1 flex-col'} id={'content'}>
      <PageHeader
        backRoute={'../'}
        title={`${dealership?.dealership} - ${
          campaign?.client_campaign_display_name ?? ''
        }`}
        {...(Boolean(agency?.show_budget_type) && {
          infoPill: {
            type: 'warning',
            content: `Budget: ${
              agency?.show_budget_type === 1
                ? formatCurrency(campaignBudget?.demand_local_net)
                : formatCurrency(campaignBudget?.gross_revenue)
            }`,
          },
        })}
        cardsOptions={[
          {
            title: 'CTR',
            content: `${
              Math.round(
                (overallMetrics?.clicks / (overallMetrics?.impressions || 1)) * 10000,
              ) / 100
            }%`,
            color: 'bg-palatinate-blue/30',
          },
          {
            title: 'Booked Impressions',
            content: formatNumber(overallMetrics?.booked_impressions || 0),
            color: 'bg-secondary-malachite-green/30',
          },
          {
            title: 'Delivery',
            content: `${
              Math.round(
                (overallMetrics?.impressions / overallMetrics?.booked_impressions) * 1000,
              ) / 10 || 0
            }%`,
            color: 'bg-dodger-blue/30',
          },
          ...(Number(phoneCalls?.pagination?.total) > 0
            ? [
                {
                  title: 'Phone Calls',
                  content: formatNumber(phoneCalls?.pagination?.total || 0),
                  color: 'bg-palatinate-blue/30',
                },
              ]
            : []),
          {
            title: ({ collapsed }) => {
              return collapsed ? (
                <div>Attributed Sales</div>
              ) : (
                <div className={'flex flex-row'}>
                  <span>Attributed Sales</span>
                  <span className={'absolute right-0'}>
                    <VehicleIcon />
                  </span>
                </div>
              )
            },
            content: formatNumber(overallMetrics?.sales || 0),
            color: '#FFFFFF',
            border: true,
          },
        ]}
        rightSlot={({ hidden }) => (
          <div className={'print:hidden flex flex-row gap-2'}>
            <DateRangePicker
              collapsed={hidden}
              filters={filters}
              onChange={onDateRangeChange}
            />
            <Button
              disabled={isDownloadingReport}
              collapsed={hidden}
              leftSlot={<DownloadIcon className={'fill-white'} />}
              onClick={async (e) => {
                e.preventDefault()
                const toastId = toast.loading('Generating report...')
                GA4.sendEvent({
                  event_name: 'export_pdf',
                  event_params: {
                    campaign_type: 'DYD',
                    campaign_id: campaign?.client_campaign_id,
                  },
                })
                mtm.sendEvent('Export', 'PDF', 'DYD', campaign?.client_campaign_id)
                await downloadReport(undefined, {
                  async onSuccess(data) {
                    toast.success('Report generated successfully', { id: toastId })
                    const blob = await data.blob()
                    const url = window.URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `${campaign?.client_campaign_display_name}_${filters?.dateRange?.startDate}_-_${filters?.dateRange?.endDate}.pdf.pdf`
                    document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                    a.click()
                    a.remove()
                  },
                })
              }}>
              Export as PDF
            </Button>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        <div className={'mt-12 mb-6'} />
        {children}
      </div>
    </div>
  )
}

const DynamicDisplayCampaign = (): JSX.Element => {
  const { campaign_id } = useParams()
  const { dateRange } = useDateRange()
  const [queryParams, setQueryParams] = useSearchParams()
  const dealership = useDealership()
  const [downloadingTable, setDownloadingTable] = useState<boolean>(false)
  const { data: campaign } = useGetDisplayCampaignById({ campaign_id: campaign_id })

  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })

  const {
    data: { metrics } = { metrics: [] },
    isLoading: isLoadingInventoryReports,
    isPreviousData: isPreviousInventoryReportsData,
  } = useGetDYDReport({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const {
    data: { vehicleStats } = {},
    data: { averages = { impressions: 1, clicks: 1, ctr: 1 } } = {},
    isLoading: isLoadingTopVehicles,
    isPreviousData: isPreviousTopVehicles,
  } = useGetDynamicDisplayTopVehicles({
    campaign_id: campaign_id,
    filters: {
      start_date: filters.dateRange.startDate,
      end_date: filters.dateRange.endDate,
    },
  })

  const { data: performanceStats, isLoading: isLoadingPerformanceStats } =
    useGetPerformanceStats({
      filters: {
        start_date: filters.dateRange.startDate,
        end_date: filters.dateRange.endDate,
      },
    })

  const { data: creativeMetrics, isLoading: isLoadingCreativeMetrics } =
    useGetDisplayCreativeMetrics({
      filters: {
        campaign_id: campaign_id,
        start_date: filters?.dateRange?.startDate,
        end_date: filters?.dateRange?.endDate,
      },
    })

  const { data: phoneCalls } = useGetCampaignCallRecordings({
    filters: {
      campaign_id: campaign_id,
      start_date: filters?.dateRange?.startDate,
      end_date: filters?.dateRange?.endDate,
    },
  })

  const adPreviews = useMemo(() => {
    if (!creativeMetrics?.data || !Array.isArray(creativeMetrics.data)) return []

    const iteratee = (object: DisplayCreative): string | undefined => object.image_url
    return take(
      uniqBy(
        creativeMetrics.data.filter(({ image_url }: { image_url?: string }) => image_url),
        iteratee,
      ),
      3,
    )
  }, [creativeMetrics])

  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  useEffect(() => {
    if (
      !isLoadingCreativeMetrics &&
      !isLoadingTopVehicles &&
      !isLoadingInventoryReports &&
      !isPreviousInventoryReportsData &&
      !isLoadingPerformanceStats
    ) {
      // set 2 second timeout to give images time to load
      const timeout = setTimeout(() => {
        const content = document.getElementById('content')
        if (content) content.className = `${content.className} loaded`
      }, 5000)
      return () => {
        clearTimeout(timeout)
      }
    }
  }, [
    isLoadingInventoryReports,
    isPreviousInventoryReportsData,
    isLoadingTopVehicles,
    isLoadingCreativeMetrics,
    isLoadingPerformanceStats,
  ])

  const vehicleMetricsColumns = useMemo(
    () => [
      columnHelper.accessor('description', {
        header: 'Vehicle',
        cell: ({ row }) => (
          <Link
            className={'text-primary-persian-blue font-bold'}
            to={`${
              row.original.vin_number
                ? `../../inventory/vehicles/${row.original.vin_number}`
                : '#'
            }`}>
            <span>{row.original.description}</span>
          </Link>
        ),
      }),
      columnHelper.accessor('vin_number', {
        header: 'VIN',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('facebook_product_vendor_id', {
        header: 'Stock',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('vehicle_state', {
        header: 'State',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      columnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: ({ getValue }) => formatNumber(getValue() ?? 0),
      }),
      columnHelper.accessor('ctr', {
        header: 'CTR',
        cell: ({ getValue }) => `${getValue()}%`,
      }),
    ],
    [],
  )

  const phoneCallsColumns = useMemo(
    () => [
      callsColumnHelper.accessor('phone_number', {
        header: 'Caller',
        cell: (info) => info.getValue(),
      }),
      callsColumnHelper.accessor('phone_call_vendor_id', {
        header: 'Recording',
        cell: (info) => (
          <audio controls>
            <source
              src={`https://phone-call-recordings.s3.us-east-1.amazonaws.com/${info.getValue()}.mp3`}
              type="audio/mpeg"
            />
          </audio>
        ),
      }),
      callsColumnHelper.accessor('datetime', {
        header: 'Date',
        cell: (info) => dayjs(info.getValue()).format(`MMMM D, YYYY - h:mm a`),
      }),
      callsColumnHelper.accessor('duration', {
        header: 'Duration',
        cell: (info) => info.getValue(),
      }),
    ],
    [],
  )

  return (
    <DynamicDisplayCampaignTemplate filters={filters} setFilters={setFilters}>
      <div className={'flex flex-col gap-10 pb-8'}>
        <div className={'flex flex-col gap-8'}>
          <div className={'flex grid grid-cols-2 gap-5'}>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Impressions</span>
                <span>
                  {!isLoadingInventoryReports &&
                    new Intl.NumberFormat('en-US').format(
                      metrics
                        ?.map((metric: { impressions: number }) => metric.impressions)
                        .reduce((acc: number, cur: number) => acc + cur, 0),
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'green-to-cyan'}
                  yKey={'impressions'}
                  isLoading={isLoadingInventoryReports}
                  isPreviousData={isPreviousInventoryReportsData}
                />
              </div>
            </div>
            <div className={'min-h-[270px] border-2 rounded-xl'}>
              <div
                className={
                  'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
                }>
                <span>Total Clicks</span>
                <span>
                  {!isLoadingInventoryReports &&
                    new Intl.NumberFormat('en-US').format(
                      metrics
                        ?.map(
                          (metric: { impressions: number; clicks: number }) =>
                            metric.clicks,
                        )
                        .reduce((acc: number, cur: number) => acc + cur, 0),
                    )}
                </span>
              </div>
              <div className={'h-[270px] p-2'}>
                <LineChart
                  data={metrics ?? []}
                  gradient={'red-to-orange'}
                  yKey={'clicks'}
                  isLoading={isLoadingInventoryReports}
                  isPreviousData={isPreviousInventoryReportsData}
                />
              </div>
            </div>
          </div>

          <ConversionAttributionMetrics filters={filters} />
          <div className={'flex grid grid-cols-3 gap-5'}>
            {isLoadingTopVehicles || isPreviousTopVehicles ? (
              <div
                className={
                  'px-5 min-h-[300px] border-2 rounded-xl items-center justify-center flex'
                }>
                Loading...
              </div>
            ) : (
              <div className={'px-5 min-h-[300px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 flex justify-between font-bold text-md text-primary-xiketic'
                  }>
                  <span>Highest CTR vehicle</span>
                  <span>
                    <Pill type={'success'}>
                      {Math.round((vehicleStats?.highestCtr?.ctr ?? 0) * 10000) / 100}%
                    </Pill>
                  </span>
                </div>
                <div
                  className={
                    'pb-2 flex justify-between font-bold text-xs text-green-primary '
                  }>
                  <span>
                    <strong>{`${
                      Math.round(
                        (Number(vehicleStats?.highestCtr?.ctr) - Number(averages?.ctr)) *
                          10000,
                      ) / 100
                    }%`}</strong>{' '}
                    more than other cars
                  </span>
                </div>

                <motion.img
                  loading="lazy"
                  src={
                    vehicleStats?.highestCtr?.log_image_url?.match(
                      /https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/,
                    )?.[0]
                  }
                  className={`rounded-xl w-fit h-fit object-cover snap-center`}
                />

                <div
                  className={
                    'pt-5 pb-2 flex justify-between font-bold text-md text-primary-xiketic'
                  }>
                  <span>{vehicleStats?.highestCtr?.description}</span>
                </div>
                <div className={'pb-7 flex justify-between text-xs text-primary-xiketic'}>
                  <span>VIN: {vehicleStats?.highestCtr?.log_vin_number}</span>
                </div>
              </div>
            )}
            {isLoadingTopVehicles || isPreviousTopVehicles ? (
              <div
                className={
                  'px-5 min-h-[300px] border-2 rounded-xl items-center justify-center flex'
                }>
                Loading...
              </div>
            ) : (
              <div className={'px-5 min-h-[300px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 flex justify-between font-bold text-md text-primary-xiketic'
                  }>
                  <span>Most clicked vehicle</span>
                  <span>
                    <Pill type={'success'}>
                      {Intl.NumberFormat('en-US').format(
                        vehicleStats?.mostClicked?.clicks ?? 0,
                      )}{' '}
                      clicks
                    </Pill>
                  </span>
                </div>
                <div
                  className={
                    'pb-2 flex justify-between font-bold text-xs text-green-primary '
                  }>
                  <span>
                    <strong>
                      {`${
                        Math.round(
                          (Number(vehicleStats?.mostClicked?.clicks) /
                            Number(averages?.clicks)) *
                            10,
                        ) / 10
                      }X`}
                    </strong>{' '}
                    more than other cars
                  </span>
                </div>
                <motion.img
                  loading="lazy"
                  src={
                    vehicleStats?.mostClicked?.log_image_url?.match(
                      /https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/,
                    )?.[0]
                  }
                  className={`rounded-xl w-fit h-fit object-cover snap-center`}
                />

                <div
                  className={
                    'pt-5 pb-2 flex justify-between font-bold text-md text-primary-xiketic'
                  }>
                  <span>{vehicleStats?.mostClicked?.description}</span>
                </div>
                <div className={'pb-7 flex justify-between text-xs text-primary-xiketic'}>
                  <span>VIN: {vehicleStats?.mostClicked?.log_vin_number}</span>
                </div>
              </div>
            )}
            {isLoadingTopVehicles || isPreviousTopVehicles ? (
              <div
                className={
                  'px-5 min-h-[300px] border-2 rounded-xl items-center justify-center flex'
                }>
                Loading...
              </div>
            ) : (
              <div className={'px-5 min-h-[300px] border-2 rounded-xl'}>
                <div
                  className={
                    'pt-5 pb-2 flex justify-between font-bold text-md text-primary-xiketic'
                  }>
                  <span>Most viewed vehicle</span>
                  <span>
                    <Pill type={'success'}>
                      {Intl.NumberFormat('en-US').format(
                        vehicleStats?.mostViewed?.impressions ?? 0,
                      )}{' '}
                      views
                    </Pill>
                  </span>
                </div>
                <div
                  className={
                    'pb-2 flex justify-between font-bold text-xs text-green-primary '
                  }>
                  <span>
                    <strong>{`${
                      Math.round(
                        (Number(vehicleStats?.mostViewed?.impressions) /
                          Number(averages?.impressions)) *
                          10,
                      ) / 10
                    }X`}</strong>{' '}
                    more than other cars
                  </span>
                </div>
                <motion.img
                  loading="lazy"
                  src={
                    vehicleStats?.mostViewed?.image_url?.match(
                      /https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/,
                    )?.[0] ??
                    vehicleStats?.mostViewed?.log_image_url?.match(
                      /https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/,
                    )?.[0]
                  }
                  className={`rounded-xl w-fit h-fit object-cover snap-center`}
                />
                <div
                  className={
                    'pt-5 pb-2 flex justify-between font-bold text-md text-primary-xiketic'
                  }>
                  <span>{vehicleStats?.mostViewed?.description}</span>
                </div>
                <div className={'pb-7 flex justify-between text-xs text-primary-xiketic'}>
                  <span>VIN: {vehicleStats?.mostViewed?.log_vin_number}</span>
                </div>
              </div>
            )}
          </div>

          <div className={'pb-5 border-2 rounded-lg flex flex-col gap-3'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex justify-between font-bold text-sm text-primary-xiketic'
              }>
              <span>CTR</span>
              <div className={'flex items-center gap-2'}>
                {metrics.length > 0 &&
                  `${
                    Math.round(
                      (metrics.map((item) => item.clicks).reduce((a, b) => a + b) *
                        10000) /
                        metrics.map((item) => item.impressions).reduce((a, b) => a + b),
                    ) / 100
                  }%`}
              </div>
            </div>
            <div className={'w-full h-[320px] p-2'}>
              <LineChart
                data={metrics ?? []}
                gradient={'blue-to-purple'}
                yKey={'ctr'}
                isLoading={isLoadingInventoryReports}
                isPreviousData={isPreviousInventoryReportsData}
              />
            </div>
          </div>
          {Number(phoneCalls?.pagination?.total) > 0 && (
            <div className={'flex flex-col gap-4'}>
              <div className="flex flex-row justify-between items-center">
                <span className={'font-bold text-primary-xiketic text-sm'}>
                  Phone calls
                </span>
                <Button
                  disabled={downloadingTable}
                  variant="primary"
                  onClick={async (e) => {
                    e.preventDefault()
                    const toastId = toast.loading('Downloading...')
                    setDownloadingTable(true)
                    try {
                      const headers = phoneCallsColumns.map((column) => ({
                        accessorKey:
                          // @ts-ignore
                          column.accessorKey === 'phone_call_vendor_id'
                            ? 'recording'
                            : // @ts-ignore
                              column.accessorKey,
                        header: column.header,
                      }))
                      await downloadCSV({
                        url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/campaigns/search/${campaign_id}/calls`,
                        fetchOptions: {
                          campaign_id: campaign_id,
                          start_date: filters?.dateRange?.startDate,
                          end_date: filters?.dateRange?.endDate,
                        },
                        headers: headers,
                        fileName: `${campaign.client_campaign_display_name}_phone_calls_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                      })
                      toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                      setDownloadingTable(false)
                    } catch (err) {
                      toast.error('Failed to download table at this time.', {
                        id: toastId,
                        duration: 5000,
                      })
                      setDownloadingTable(false)
                    }
                  }}>
                  Export CSV
                </Button>
              </div>
              <Table
                emptyLabel={'No phone calls found'}
                columns={phoneCallsColumns}
                filters={{
                  campaign_id: campaign_id,
                  start_date: filters?.dateRange?.startDate,
                  end_date: filters?.dateRange?.endDate,
                }}
                customPageSize={10}
                defaultSorting={[{ id: 'datetime', desc: true }]}
                fetchFn={useGetCampaignCallRecordings}
              />
            </div>
          )}
          {Boolean(campaign?.show_site_list) && (
            <div className={'flex flex-col gap-4'}>
              <span className={'font-bold text-primary-xiketic text-sm'}>
                Top Performing Sites
              </span>
              <SiteListTable />
            </div>
          )}
          {!isLoadingInventoryReports && (
            <div className={'flex flex-col gap-4'}>
              <div className="flex flex-row justify-between items-center">
                <span className={'font-bold text-primary-xiketic text-sm'}>
                  Top Performing Vehicles
                </span>
                <Button
                  disabled={downloadingTable}
                  variant="primary"
                  onClick={async (e) => {
                    e.preventDefault()
                    const toastId = toast.loading('Downloading...')
                    setDownloadingTable(true)
                    try {
                      const headers = vehicleMetricsColumns.map((column) => ({
                        // @ts-ignore
                        accessorKey: column.accessorKey,
                        header: column.header,
                      }))
                      await downloadCSV({
                        url: `/agencies/${dealership?.organization_id}/dealership/${dealership?.id}/campaigns/dyd/${campaign_id}/vehicles`,
                        fetchOptions: {
                          campaign_id: campaign_id,
                          start_date: filters?.dateRange?.startDate,
                          end_date: filters?.dateRange?.endDate,
                        },
                        headers: headers,
                        fileName: `${campaign.client_campaign_display_name}_top_performing_vehicles_${filters.dateRange.startDate}-${filters.dateRange.endDate}.csv`,
                      })
                      toast.success('Downloaded table!', { id: toastId, duration: 5000 })
                      setDownloadingTable(false)
                    } catch (err) {
                      toast.error('Failed to download table at this time.', {
                        id: toastId,
                        duration: 5000,
                      })
                      setDownloadingTable(false)
                    }
                  }}>
                  Export CSV
                </Button>
              </div>
              <Table
                emptyLabel={'No vehicles found'}
                columns={vehicleMetricsColumns}
                filters={{
                  campaign_id: campaign_id,
                  start_date: filters?.dateRange?.startDate,
                  end_date: filters?.dateRange?.endDate,
                }}
                defaultSorting={[
                  {
                    id: 'impressions',
                    desc: true,
                  },
                ]}
                fetchFn={useGetDynamicDisplayCampaignVehicleMetrics}
              />
            </div>
          )}
          {adPreviews.length > 0 && Boolean(campaign?.show_ad_previews) && (
            <div className={`flex grid grid-cols-3 gap-5`}>
              {adPreviews.map((ad, index) => {
                return (
                  <div key={ad.image_url} className={'py-5 rounded-xl'}>
                    <div
                      className={
                        'pb-4 border-b-2 flex justify-between font-bold text-md text-primary-xiketic'
                      }>
                      <span>Ad Preview #{index + 1}</span>
                    </div>
                    <div
                      className={
                        'pt-5 pb-2 flex justify-between font-bold text-sm text-primary-xiketic'
                      }>
                      {ad.creative_name}
                    </div>
                    <div
                      className={
                        'flex pb-3 justify-between text-xs text-primary-xiketic'
                      }>
                      <span>Ad Size: {ad.creative_size}</span>
                    </div>
                    <motion.div
                      className={'flex items-center justify-center'}
                      style={{
                        width: Number(ad.creative_size?.split('x')[0]),
                        height: Number(ad.creative_size?.split('x')[1]),
                      }}>
                      {
                        <iframe
                          src={ad.image_url}
                          title="Ad Preview"
                          className="flex w-full h-full "
                        />
                      }
                    </motion.div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </DynamicDisplayCampaignTemplate>
  )
}

export default DynamicDisplayCampaign
